import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isReconnecting: boolean;
  lastOnlineAt?: Date;
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | undefined>(
    navigator.onLine ? new Date() : undefined
  );

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setIsReconnecting(false);
      setLastOnlineAt(new Date());
      console.log('🟢 Network: Back online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsReconnecting(true);
      console.log('🔴 Network: Gone offline');
      
      // After 30 seconds offline, stop showing "reconnecting"
      setTimeout(() => {
        if (!navigator.onLine) {
          setIsReconnecting(false);
        }
      }, 30000);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Test actual connectivity periodically when offline
    const connectivityCheck = setInterval(() => {
      if (!navigator.onLine) {
        fetch('/api/health', { 
          method: 'HEAD',
          cache: 'no-cache',
          signal: AbortSignal.timeout(5000) 
        })
        .then(() => {
          if (!isOnline) {
            handleOnline();
          }
        })
        .catch(() => {
          // Still offline
        });
      }
    }, 15000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityCheck);
    };
  }, [isOnline]);

  return {
    isOnline,
    isReconnecting,
    lastOnlineAt,
  };
}

export default useNetworkStatus;